import {Module, ModuleItem} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import featureFlags from '@dms/feature-flags';
import {workshopRoutes} from '@dms/routes';
import {permissions, useImporterReportingIntegrations} from '@dms/shared';

import {buildArray} from 'shared/web';

import {CreateServiceCase} from './pages/CreateServiceCase/CreateServiceCase';
import {MercedesMraReport} from './pages/MercedesMraReport/MercedesMraReport';
import {PostponedRequests} from './pages/PostponedRequests/PostponedRequests';
import {Report5starCstar} from './pages/Report5starCstar/Report5starCstar';
import {ServiceCampaignDetail} from './pages/ServiceCampaignDetail/ServiceCampaignDetail';
import {ServiceCampaignsOverview} from './pages/ServiceCampaigns/ServiceCampaignsOverview';
import {ServiceCaseDetail} from './pages/ServiceCaseDetail/ServiceCaseDetail';
import {WorkshopInterest} from './pages/WorkshopInterest/WorkshopInterest';
import {WorkshopInterestCreate} from './pages/WorkshopInterestCreate/WorkshopInterestCreate';
import {WorkshopInterestDetail} from './pages/WorkshopInterestDetail/WorkshopInterestDetail';
import {WorkshopOverview} from './pages/WorkshopOverview/WorkshopOverview';
import {WorkshopReport} from './pages/WorkshopReport/WorkshopReport';

export function useWorkshopModule(): Module {
  const importerReportingIntegrations = useImporterReportingIntegrations();

  const content = buildArray<ModuleItem>([
    {
      id: 'workshop-serviceCases',
      routerProps: {
        path: workshopRoutes.serviceCases,
        element: <WorkshopOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.serviceCases',
        icon: 'sidebar/workshop_service_cases',
      },
    },
    {
      id: 'workshop-serviceOrders',
      routerProps: {
        path: workshopRoutes.serviceOrders,
        element: <WorkshopOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.serviceOrders',
        icon: 'sidebar/workshop_service_orders',
      },
    },
    {
      id: 'workshop-directSales',
      routerProps: {
        path: workshopRoutes.directSales,
        element: <WorkshopOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.directSales',
        icon: 'sidebar/workshop_direct_sales',
      },
    },
    {
      id: 'workshop-labour-operation-settings',
      routerProps: {
        path: workshopRoutes.labourOperationSettings,
        element: <WorkshopOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.labourOperationSettings',
        icon: 'sidebar/workshop_labour_operation_settings',
      },
    },
    {
      id: 'workshop-interest',
      routerProps: {
        path: workshopRoutes.interest,
        element: <WorkshopInterest />,
      },
      layoutProps: {
        title: 'page.workshop.labels.interest',
        icon: 'sidebar/interest',
      },
      requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
    },
    {
      id: 'workshop-interestCreate',
      routerProps: {
        path: workshopRoutes.interestCreate,
        element: <WorkshopInterestCreate />,
      },
      requiredPermission: permissions.serviceCaseRead,
      requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
      layoutProps: {
        title: 'entity.interest.labels.newInterest',
        isPartOfCreateMenuItem: true,
      },
    },
    {
      id: 'workshop-interestDetail',
      routerProps: {
        path: workshopRoutes.interestDetail,
        element: <WorkshopInterestDetail />,
      },
      requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
    },
    {
      id: 'workshop-draftServiceCase',
      routerProps: {
        path: workshopRoutes.draftServiceCase,
        element: <CreateServiceCase />,
      },
    },
    {
      id: 'workshop-createServiceCase',
      routerProps: {
        path: workshopRoutes.createServiceCase,
        element: <CreateServiceCase />,
      },
      requiredPermission: permissions.serviceCaseRead,
      layoutProps: {
        title: 'page.workshop.labels.newServiceCase',
        isPartOfCreateMenuItem: true,
      },
    },
    {
      id: 'workshop-serviceCase-detail',
      routerProps: {
        path: workshopRoutes.serviceCaseDetail,
        element: <ServiceCaseDetail />,
      },
    },
    {
      id: 'workshop-service-campaigns',
      routerProps: {
        path: workshopRoutes.serviceCampaigns,
        element: <ServiceCampaignsOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.serviceCampaigns',
        icon: 'sidebar/workshop_service_campaigns',
      },
    },
    {
      id: 'workshop-service-campaigns-Hyundai',
      routerProps: {
        path: workshopRoutes.serviceCampaignsUnfinished,
        element: <ServiceCampaignsOverview />,
      },
    },
    {
      id: 'workshop-service-campaign-detail',
      routerProps: {
        path: workshopRoutes.serviceCampaignDetail,
        element: <ServiceCampaignDetail />,
      },
    },
    {
      id: 'workshop-postponed-requests',
      routerProps: {
        path: workshopRoutes.postponedRequests,
        element: <PostponedRequests />,
      },
      layoutProps: {
        title: 'page.workshop.labels.postponedRequests',
        icon: 'sidebar/workshop_postponed_requests',
      },
    },
    {
      id: 'workshop-reporting',
      routerProps: {
        path: workshopRoutes.reporting,
        element: <WorkshopReport />,
      },
      layoutProps: {
        title: 'analytics.label.workshopReports',
        icon: 'sidebar/analytics',
      },
    },
    {
      id: 'workshop-reporting-tab',
      routerProps: {
        path: workshopRoutes.reportingTab,
        element: <WorkshopReport />,
      },
    },
  ])
    .when(
      importerReportingIntegrations.isMercedesMraEnabled,
      {
        id: 'workshop-reporting-mercedes-mra',
        routerProps: {
          path: workshopRoutes.reportingMercedesMra,
          element: <MercedesMraReport />,
        },
        layoutProps: {
          title: 'analytics.label.mercedesMra',
          icon: 'sidebar/analytics',
        },
      },
      {
        id: 'workshop-reporting-mercedes-mra-custom-reports',
        routerProps: {
          path: workshopRoutes.reportingMercedesMraCustomReports,
          element: <MercedesMraReport />,
        },
      },
      {
        id: 'workshop-reporting-mercedes-mra-inactive-reports',
        routerProps: {
          path: workshopRoutes.reportingMercedesMraInactiveReports,
          element: <MercedesMraReport />,
        },
      }
    )
    .when(importerReportingIntegrations.isMerc5StarEnabled, {
      id: 'workshop-reporting-5star',
      routerProps: {
        path: workshopRoutes.reportingFiveStar,
        element: <Report5starCstar type="5star" />,
      },
      layoutProps: {
        title: 'analytics.label.5starReports',
        icon: 'sidebar/analytics',
      },
    })
    .when(importerReportingIntegrations.isMercCstarEnabled, {
      id: 'workshop-reporting-cstar',
      routerProps: {
        path: workshopRoutes.reportingCStar,
        element: <Report5starCstar type="cstar" />,
      },
      layoutProps: {
        title: 'analytics.label.CstarReports',
        icon: 'sidebar/analytics',
      },
    });

  return {
    id: moduleKeys.workshop,
    layoutProps: {
      title: 'module.workshop.title',
      icon: 'sidebar/workshop',
    },
    routerProps: {
      path: workshopRoutes.overview,
      element: <WorkshopOverview />,
    },
    requiredPermission: permissions.serviceCaseRead,
    content,
  };
}
